import React, { useEffect, useState } from 'react'
import { IonPage, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import PowerOnTimeSelect from '../powerOnTimeSelect/PowerOnTimeSelect.page'
import ImportEvents from '../importEvents/ImportEvents.page'
import { useDispatch, useSelector } from 'react-redux'
import { getEKGImportSelector } from '../selectors'
import { resetEKGAfterFlowCompletes } from '../store/ekgImport.actions'
import ImportReports from '../importReports/ImportReports.page'
import EKGImportHeader from '../ekgImportHeader/EKGImportHeader.component'
import PowerOnTimeCard from '../powerOnTimeCard/powerOnTimeCard.component'
import ImportSummary from '../importSummary/ImportSummary.page'
import { useHistory } from 'react-router'
import { AuthenticationService } from '../../login/services/authentication.service'
import { getUserSelector } from '../../user/selectors'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { getApiErrorHandlerSelector } from '../../../components/apiErrorHandler/selectors'
import Logger from '../../../services/logger.service'
import { goBack } from '../../../utilities/navigation.util'

interface IEKGImportPageState {
  currentStep: number
  isUnloaded: boolean
}

const EKGImportPage: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector(getUserSelector)
  const { showToast } = useSelector(getApiErrorHandlerSelector)

  const [state, setState] = useState<IEKGImportPageState>({
    currentStep: 1,
    isUnloaded: false,
  })
  const { currentStep, isUnloaded } = state

  const { selectedPowerOnTime } = useSelector(getEKGImportSelector)

  useIonViewWillEnter(() => {
    setState((prevState) => ({
      ...prevState,
      isUnloaded: false,
    }))
  })

  useIonViewWillLeave(() => {
    dispatch(resetEKGAfterFlowCompletes())
    setState((prevState) => ({
      ...prevState,
      currentStep: 1,
      isUnloaded: true,
    }))
  })

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)
  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes])

  useEffect(() => {
    if (selectedPowerOnTime) {
      setState((prevState) => ({
        ...prevState,
        currentStep: 2,
      }))
    }
  }, [selectedPowerOnTime])

  const handleApiError = () => {
    goBack()
  }

  useEffect(() => {
    if (showToast) handleApiError()
  }, [showToast])

  const onCancel = () => {
    Logger.info('ECGImport: The user has cancelled out of the ECG flow')
    goBack()
  }

  const onPreviousStep = () => {
    setState((prevState) => ({
      ...prevState,
      currentStep: currentStep - 1,
    }))
  }

  const onNextStep = () => {
    setState((prevState) => ({
      ...prevState,
      currentStep: currentStep + 1,
    }))
  }

  const renderFlowHeader = () => {
    const ekgHeaderClass = currentStep === 1 ? 'ekg-header' : 'ekg-header-gradient'

    return (
      <div className={ekgHeaderClass}>
        <EKGImportHeader currentStep={currentStep} />
        {currentStep >= 2 && <PowerOnTimeCard powerOnTime={selectedPowerOnTime} />}
      </div>
    )
  }

  const renderCurrentStep = (): JSX.Element => {
    switch (currentStep) {
      case 1:
        return <PowerOnTimeSelect onCancel={onCancel} />
      case 2:
        return <ImportEvents onPrevious={onPreviousStep} onNext={onNextStep} onCancel={onCancel} />
      case 3:
        return <ImportReports onPrevious={onPreviousStep} onNext={onNextStep} onCancel={onCancel} />
      default:
        return <ImportSummary onPrevious={onPreviousStep} onCancel={onCancel} />
    }
  }

  if (!allowSecureRoutes || isUnloaded) return <></>

  return (
    <IonPage id='EKGImportPage' data-testid='EKGImport'>
      <PCRHeader />
      {renderFlowHeader()}
      {renderCurrentStep()}
    </IonPage>
  )
}

export default EKGImportPage
